import styled from '@emotion/styled'

import { combineAriaDescribedBy } from '../Input/ArcadeHelpText'

export interface ArcadeCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  id?: string
  helpTextId?: string
}

const CHECKBOX_HEIGHT_AND_WIDTH = '1.5rem'

const HiddenCheckbox = styled.input`
  appearance: none;
  opacity: 0;
  position: absolute;
  left: 0;
  top: 0;
  width: ${CHECKBOX_HEIGHT_AND_WIDTH};
  height: ${CHECKBOX_HEIGHT_AND_WIDTH};
  cursor: pointer;
  outline: none;

  &:disabled {
    cursor: not-allowed;
  }
`

const CheckedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 25" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 0.333496C3.58172 0.333496 0 3.91522 0 8.3335V16.3335C0 20.7518 3.58172 24.3335 8 24.3335H16C20.4183 24.3335 24 20.7518 24 16.3335V8.3335C24 3.91522 20.4183 0.333496 16 0.333496H8ZM17.916 9.18405C18.3858 8.67816 18.3565 7.88725 17.8506 7.4175C17.3447 6.94774 16.5538 6.97704 16.084 7.48292L10.6266 13.3601L9.01719 11.1069C8.61593 10.5452 7.83524 10.4151 7.27348 10.8163C6.71171 11.2176 6.5816 11.9983 6.98286 12.56L9.48286 16.06C9.70113 16.3656 10.0452 16.5569 10.42 16.5809C10.7948 16.605 11.1605 16.4592 11.416 16.1841L17.916 9.18405Z"
    />
  </svg>
)

const UncheckedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 25" fill="none">
    <path
      d="M1 8.3335C1 4.4675 4.13401 1.3335 8 1.3335H16C19.866 1.3335 23 4.4675 23 8.3335V16.3335C23 20.1995 19.866 23.3335 16 23.3335H8C4.13401 23.3335 1 20.1995 1 16.3335V8.3335Z"
      strokeWidth="2"
    />
  </svg>
)

const UncheckedIconContainer = styled.div`
  display: block;

  svg path {
    stroke: ${({ theme }) => theme.arcadeColors.component.checkboxInactiveBorderDefault};
  }

  label:has(input:checked) & {
    display: none;
  }

  label:has(input:disabled) & {
    svg path {
      stroke: ${({ theme }) => theme.arcadeColors.component.checkboxInactiveBorderDefault};
    }
  }
`

const CheckedIconContainer = styled.div`
  display: none;

  label:has(input:checked) & {
    display: block;

    svg path {
      fill: ${({ theme }) => theme.arcadeColors.component.checkboxActiveBackgroundDefault};
    }
  }

  label:has(input:disabled) & {
    svg path {
      fill: ${({ theme }) => theme.arcadeColors.component.checkboxActiveBackgroundDisabled};
    }
  }
`

const VisibleCheckbox = styled.div`
  width: ${CHECKBOX_HEIGHT_AND_WIDTH};
  height: ${CHECKBOX_HEIGHT_AND_WIDTH};
`

export default (props: ArcadeCheckboxProps) => {
  const { 'aria-describedby': ariaDescribedBy, helpTextId, ...restProps } = props
  const combinedAriaDescribedBy = combineAriaDescribedBy(ariaDescribedBy, helpTextId)

  return (
    <>
      <HiddenCheckbox
        type="checkbox"
        {...restProps}
        data-testid="hidden-checkbox"
        aria-describedby={combinedAriaDescribedBy}
      />
      <VisibleCheckbox data-testid="visible-checkbox">
        <UncheckedIconContainer data-testid="checkbox-unchecked-icon">
          <UncheckedIcon />
        </UncheckedIconContainer>
        <CheckedIconContainer data-testid="checkbox-checked-icon">
          <CheckedIcon />
        </CheckedIconContainer>
      </VisibleCheckbox>
    </>
  )
}
